import React, { useState, useEffect, useMemo, useRef } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import ActionTypeBadge from './ActionTypeBadge';
import HistoryValueCell from './HistoryValueCell';
import HistoryDetailsCell from './HistoryDetailsCell';
import { getHistoryDisplayValues, resolveStatusActionType, getHistoryDetails, resolveHistoryChangedBy } from './orderHistoryFilter';
import {
    HISTORY_PERIOD_OPTIONS,
    filterHistoryByPeriod,
    groupHistoryEntries,
    getDefaultExpandedState,
} from './orderHistoryGrouping';

const formatHistoryDate = (isoString) => {
    if (!isoString) return '—';
    const d = new Date(isoString);
    if (isNaN(d.getTime())) return '—';
    return d.toLocaleString('en-US', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
    });
};

const HistoryTableRow = ({ entry, showOrderNum = false }) => {
    const { previousValue, newValue } = getHistoryDisplayValues(entry);
    return (
        <tr>
            {showOrderNum && <td className="oh-nowrap">{entry.ordernum || '—'}</td>}
            <td className="oh-nowrap oh-col-date">{formatHistoryDate(entry.changedAt)}</td>
            <td className="oh-col-action">
                <ActionTypeBadge actionType={resolveStatusActionType(entry)} actionDetail={entry.actionDetail} />
            </td>
            <td className="oh-col-details"><HistoryDetailsCell text={getHistoryDetails(entry)} /></td>
            <td><HistoryValueCell value={previousValue} /></td>
            <td><HistoryValueCell value={newValue} /></td>
            <td className="oh-nowrap">{resolveHistoryChangedBy(entry)}</td>
        </tr>
    );
};

/**
 * Grouped, collapsible order history with period filter.
 * @param {object[]} entries - History records (newest-first from API is fine; grouping re-sorts).
 * @param {boolean} [showOrderNum=false] - Show Order # column (All Orders History).
 */
const OrderHistoryGroupedView = ({ entries, showOrderNum = false }) => {
    const [period, setPeriod] = useState('all');
    const [periodDropdownOpen, setPeriodDropdownOpen] = useState(false);
    const [customStart, setCustomStart] = useState('');
    const [customEnd, setCustomEnd] = useState('');
    const [expandedSections, setExpandedSections] = useState({});
    const filterRef = useRef(null);

    const periodLabel = HISTORY_PERIOD_OPTIONS.find((o) => o.value === period)?.label || 'All History';

    const filteredEntries = useMemo(
        () => filterHistoryByPeriod(entries, period, new Date(), customStart, customEnd),
        [entries, period, customStart, customEnd]
    );

    const sections = useMemo(
        () => groupHistoryEntries(filteredEntries),
        [filteredEntries]
    );

    useEffect(() => {
        setExpandedSections(getDefaultExpandedState(sections));
    }, [sections]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (periodDropdownOpen && filterRef.current && !filterRef.current.contains(event.target)) {
                setPeriodDropdownOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [periodDropdownOpen]);

    const toggleSection = (key) => {
        setExpandedSections((prev) => ({ ...prev, [key]: !prev[key] }));
    };

    const handlePeriodSelect = (value) => {
        setPeriod(value);
        setPeriodDropdownOpen(false);
        if (value !== 'custom') {
            setCustomStart('');
            setCustomEnd('');
        }
    };

    const colCount = showOrderNum ? 7 : 6;
    const tableClass = showOrderNum
        ? 'table table-sm table-hover align-middle mb-0 order-history-table all-orders-history-table'
        : 'table table-sm table-hover align-middle mb-0 order-history-table';

    if (!entries || entries.length === 0) {
        return null;
    }

    return (
        <div className="oh-grouped-view">
            <div className="oh-period-filter-bar d-flex align-items-center gap-3 mb-3 flex-wrap">
                <div className="d-flex align-items-center gap-2">
                    <span className="oh-period-filter-label">Show</span>
                    <div className="filter-tag-selector-wrapper oh-period-filter" ref={filterRef}>
                        <div
                            className="form-control d-flex align-items-center justify-content-between filter-tag-selector"
                            onClick={() => setPeriodDropdownOpen(!periodDropdownOpen)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    setPeriodDropdownOpen(!periodDropdownOpen);
                                }
                            }}
                        >
                            <span className="filter-tag-label">{periodLabel}</span>
                            <i className={`fa fa-chevron-${periodDropdownOpen ? 'up' : 'down'} filter-tag-chevron`} />
                        </div>
                        {periodDropdownOpen && (
                            <div className="position-absolute w-100 filter-tag-dropdown oh-period-dropdown">
                                {HISTORY_PERIOD_OPTIONS.map((option) => (
                                    <div
                                        key={option.value}
                                        className={`p-2 d-flex align-items-center filter-tag-dropdown-item ${period === option.value ? 'selected' : ''}`}
                                        onClick={() => handlePeriodSelect(option.value)}
                                    >
                                        <span className="filter-tag-label">{option.label}</span>
                                        {period === option.value && (
                                            <span className="ms-auto filter-tag-checkmark">✓</span>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {period === 'custom' && (
                    <div className="d-flex align-items-center gap-2 oh-custom-date-range flex-wrap">
                        <div className="d-flex align-items-center gap-2">
                            <span className="oh-period-filter-label">From</span>
                            <DatePicker
                                dateFormat="yyyy-MM-dd"
                                className="form-control oh-date-input"
                                selected={customStart ? new Date(`${customStart}T00:00:00`) : null}
                                placeholderText="YYYY-MM-DD"
                                onChange={(date) => {
                                    if (!date) { setCustomStart(''); return; }
                                    const y = date.getFullYear();
                                    const m = String(date.getMonth() + 1).padStart(2, '0');
                                    const d = String(date.getDate()).padStart(2, '0');
                                    setCustomStart(`${y}-${m}-${d}`);
                                }}
                                isClearable
                                shouldCloseOnSelect
                                popperProps={{ strategy: 'fixed' }}
                                autoComplete="off"
                                maxDate={customEnd ? new Date(`${customEnd}T00:00:00`) : new Date()}
                            />
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <span className="oh-period-filter-label">To</span>
                            <DatePicker
                                dateFormat="yyyy-MM-dd"
                                className="form-control oh-date-input"
                                selected={customEnd ? new Date(`${customEnd}T00:00:00`) : null}
                                placeholderText="YYYY-MM-DD"
                                onChange={(date) => {
                                    if (!date) { setCustomEnd(''); return; }
                                    const y = date.getFullYear();
                                    const m = String(date.getMonth() + 1).padStart(2, '0');
                                    const d = String(date.getDate()).padStart(2, '0');
                                    setCustomEnd(`${y}-${m}-${d}`);
                                }}
                                isClearable
                                shouldCloseOnSelect
                                popperProps={{ strategy: 'fixed' }}
                                autoComplete="off"
                                minDate={customStart ? new Date(`${customStart}T00:00:00`) : null}
                                maxDate={new Date()}
                            />
                        </div>
                    </div>
                )}
            </div>

            {filteredEntries.length === 0 ? (
                <div className="text-center text-muted py-4">
                    No history records match the selected period.
                </div>
            ) : (
                <table className={tableClass}>
                    <colgroup>
                        {showOrderNum && <col className="oh-col-order" />}
                        <col className="oh-col-date" />
                        <col className="oh-col-action" />
                        <col className="oh-col-details" />
                        <col className="oh-col-prev" />
                        <col className="oh-col-new" />
                        <col className="oh-col-user" />
                    </colgroup>
                    <thead>
                        <tr>
                            {showOrderNum && <th>Order #</th>}
                            <th className="oh-col-date">Date &amp; Time</th>
                            <th className="oh-col-action">Action</th>
                            <th>Details</th>
                            <th>Previous Value</th>
                            <th>New Value</th>
                            <th>Changed By</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sections.map((section) => {
                            const isExpanded = expandedSections[section.key];
                            const changeLabel = section.count === 1 ? 'change' : 'changes';
                            return (
                                <React.Fragment key={section.key}>
                                    <tr
                                        className={`oh-section-header ${isExpanded ? 'oh-section-expanded' : 'oh-section-collapsed'}`}
                                        onClick={() => toggleSection(section.key)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                toggleSection(section.key);
                                            }
                                        }}
                                    >
                                        <td colSpan={colCount}>
                                            <span className="oh-section-arrow" aria-hidden="true">
                                                {isExpanded ? '▼' : '▶'}
                                            </span>
                                            <span className="oh-section-title">{section.title}</span>
                                            <span className="oh-section-count">
                                                ({section.count} {changeLabel})
                                            </span>
                                        </td>
                                    </tr>
                                    {isExpanded && section.entries.map((entry) => (
                                        <HistoryTableRow
                                            key={showOrderNum
                                                ? `${entry.ordernum || ''}-${entry.id}`
                                                : entry.id}
                                            entry={entry}
                                            showOrderNum={showOrderNum}
                                        />
                                    ))}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default OrderHistoryGroupedView;

